import React, { useState } from 'react';
import { Button, Text } from '@mantine/core';
import {IconEyeOff, IconSignature, IconSignatureOff} from '@tabler/icons-react';
import * as Enums from '@/utils/enums';
import Constants from '@/utils/constants';
import FormSignatureModal from '../FormSignatureModal';
import { FieldProps, SignaturePreview } from '../form-helpers';
import styles from './FormFieldSignature.module.css';

export type FormFieldSignatureProps = Omit<FieldProps, 'value' | 'onChange'> & {
    value: string | null;
    onChange: (signatureID: string) => void;
    ensurePersisted: () => Promise<void>;
    signatureTemplateID: string;
    itemID: string;
    resolveSignaturePreview?: (signatureID: string) => SignaturePreview | null;
    onSignatureSaved?: (signature: any) => void;
};

function formatSignedDate(iso: string): string {
    if (!iso) return '';
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString();
}

function FormFieldSignature({ label, value, onChange, ensurePersisted, signatureTemplateID, itemID, disabled, error, resolveSignaturePreview, onSignatureSaved }: FormFieldSignatureProps) {
    const [showModal, setShowModal] = useState(false);

    const open = async () => {
        await ensurePersisted();
        setShowModal(true);
    };

    const preview = value ? resolveSignaturePreview?.(value) ?? null : null;

    return (
        <div style={{ marginTop: '0.5rem' }}>
            <Text size="sm" fw={500} mb={4}>{label}</Text>

            {preview ? (
                <div className={styles.thumbWrap}>
                    {preview.hidden ? (
                        // `SignatureTemplate.HideSignature` — the template is configured to never
                        // reveal the captured signature image back to whoever fills out the form
                        // (e.g. a customer shouldn't see a technician's signature). Show a
                        // placeholder instead of fetching/rendering the actual image.
                        <div className={styles.thumbHidden} data-testid="form-field-signature-thumbnail-hidden">
                            <IconSignature size={30} color="var(--mantine-color-scBlue-5)" stroke={1.6} />
                        </div>
                    ) : (
                        // A non-hidden signature stays viewable even once the form (and every other
                        // field) is disabled — `disabled` only turns off *re-signing* (the "Sign
                        // Here" empty state below still respects it fully). `FormSignatureModal`
                        // opens read-only for this: `ensurePersisted` no-ops on a completed form,
                        // and whether re-sign is offered inside is `SignatureTemplate.AllowResign`,
                        // unrelated to this field's own disabled state.
                        <img src={preview.url} alt="Signature preview" className={styles.thumb} data-testid="form-field-signature-thumbnail" onClick={open} />
                    )}
                    <div className={styles.meta}>
                        <Text size="xs" c="scBlue">Signed by {preview.signedBy} on {formatSignedDate(preview.signedDate)}</Text>
                        {preview.hidden ? (
                            <Text size="xs" c="dimmed" fs="italic">Signature hidden</Text>
                        ) : (
                            <Button variant="subtle" size="compact-sm" px={0} onClick={open}>
                                {disabled ? 'View signature' : 'View / re-sign'}
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div
                    className={`${styles.signArea} ${disabled ? styles.signAreaDisabled : ''}`}
                    role={disabled ? undefined : 'button'}
                    tabIndex={disabled ? undefined : 0}
                    aria-label={value ? 'View Signature' : 'Sign Here'}
                    onClick={disabled ? undefined : open}
                    onKeyDown={disabled ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') open(); }}
                >
                    <IconSignature size={20} stroke={1.6} />
                    <Text size="xs" fw={600} ta="center">
                        {value ? 'View Signature' : 'Sign Here'}
                    </Text>
                </div>
            )}

            {error && <Text size="xs" c="yellow.6" mt={4}>{error}</Text>}

            {showModal && (
                <FormSignatureModal
                    id={value ?? undefined}
                    request={value ? undefined : {
                        ItemID: itemID,
                        SignatureKey: Constants.signatureKeys.FormSignature,
                        SignatureTemplateID: signatureTemplateID,
                        Title: '',
                        Module: Enums.Module.FormHeader,
                    }}
                    onUpdate={(signature: any) => {
                        if (signature?.AttachmentID) onChange(signature.ID);
                        // A re-sign reuses the same signature ID (only its Attachment changes), so
                        // `resolveSignaturePreview`'s ID-keyed cache never gets a fresh fetch on its
                        // own — push the just-saved record in directly instead of waiting on that.
                        onSignatureSaved?.(signature);
                    }}
                    onDismiss={() => setShowModal(false)}
                />
            )}
        </div>
    );
}

export default React.memo(FormFieldSignature);
